import { useState } from 'react';
import type { ComponentType, SVGProps } from 'react';
import {
  ArrowTrendingUpIcon,
  CalendarDaysIcon,
  CheckIcon,
  EllipsisHorizontalIcon,
  LightBulbIcon,
  ShieldCheckIcon,
  SparklesIcon,
  WrenchScrewdriverIcon,
} from '@heroicons/react/20/solid';
import { Drawer } from '@/components/ui/drawer';
import {
  RELEASE_NOTE_KINDS,
  buildReleaseTimeline,
  countReleaseItemsByKind,
  formatReleaseDate,
  type ReleaseKindCounts,
  type ReleaseNote,
  type ReleaseNoteKind,
} from '@/config/releaseNotes';

const TIMELINE_VISIBLE_COUNT = 3;

interface ReleaseKindMeta {
  label: string;
  pluralLabel: string;
  Icon: ComponentType<SVGProps<SVGSVGElement>>;
  /** Testo e sfondo del badge/tile. */
  tone: string;
}

const RELEASE_KIND_META: Record<ReleaseNoteKind, ReleaseKindMeta> = {
  feature: {
    label: 'Novità',
    pluralLabel: 'Novità',
    Icon: SparklesIcon,
    tone: 'bg-teal-50 text-teal-700 ring-teal-600/15 dark:bg-teal-400/10 dark:text-teal-200 dark:ring-teal-300/20',
  },
  improvement: {
    label: 'Miglioria',
    pluralLabel: 'Migliorie',
    Icon: ArrowTrendingUpIcon,
    tone: 'bg-sky-50 text-sky-700 ring-sky-600/15 dark:bg-sky-400/10 dark:text-sky-200 dark:ring-sky-300/20',
  },
  fix: {
    label: 'Correzione',
    pluralLabel: 'Correzioni',
    Icon: WrenchScrewdriverIcon,
    tone: 'bg-amber-50 text-amber-800 ring-amber-600/20 dark:bg-amber-400/10 dark:text-amber-200 dark:ring-amber-300/20',
  },
  security: {
    label: 'Sicurezza',
    pluralLabel: 'Sicurezza',
    Icon: ShieldCheckIcon,
    tone: 'bg-indigo-50 text-indigo-700 ring-indigo-600/15 dark:bg-indigo-400/10 dark:text-indigo-200 dark:ring-indigo-300/20',
  },
};

function describeKindCounts(counts: ReleaseKindCounts): string {
  return RELEASE_NOTE_KINDS
    .filter((kind) => counts[kind] > 0)
    .map((kind) => `${counts[kind]} ${(counts[kind] === 1 ? RELEASE_KIND_META[kind].label : RELEASE_KIND_META[kind].pluralLabel).toLowerCase()}`)
    .join(', ');
}

/** Riepilogo compatto per categoria: solo icona e numero, etichetta per screen reader e tooltip. */
function ReleaseKindCountChips({ counts }: { counts: ReleaseKindCounts }) {
  const kinds = RELEASE_NOTE_KINDS.filter((kind) => counts[kind] > 0);
  if (kinds.length === 0) return null;
  const description = describeKindCounts(counts);

  return (
    <span className="mt-1.5 flex flex-wrap gap-1" title={description}>
      <span className="sr-only">{description}</span>
      {kinds.map((kind) => {
        const { Icon, tone } = RELEASE_KIND_META[kind];
        return (
          <span key={kind} aria-hidden="true" className={`inline-flex items-center gap-0.5 rounded-md px-1 py-0.5 text-[10px] font-semibold tabular-nums ring-1 ring-inset ${tone}`}>
            <Icon className="h-3 w-3" />
            {counts[kind]}
          </span>
        );
      })}
    </span>
  );
}

interface ReleaseNotesCenterProps {
  open: boolean;
  availableReleases: ReleaseNote[];
  selectedVersion: string | null;
  currentVersion: string;
  onClose: () => void;
  onSelectRelease: (version: string) => void;
  onMarkCurrentReleaseAsRead: () => void;
  onStartCurrentRelease: () => void;
}

export default function ReleaseNotesCenter({
  open,
  availableReleases,
  selectedVersion,
  currentVersion,
  onClose,
  onSelectRelease,
  onMarkCurrentReleaseAsRead,
  onStartCurrentRelease,
}: ReleaseNotesCenterProps) {
  const [timelineExpanded, setTimelineExpanded] = useState(false);
  const selectedRelease = availableReleases.find((release) => release.version === selectedVersion) || availableReleases[0];

  if (!selectedRelease) return null;

  const selectedKindCounts = countReleaseItemsByKind([selectedRelease]);
  const selectedKinds = RELEASE_NOTE_KINDS.filter((kind) => selectedKindCounts[kind] > 0);
  const timeline = buildReleaseTimeline(availableReleases, {
    selectedVersion: selectedRelease.version,
    expanded: timelineExpanded,
    visibleCount: TIMELINE_VISIBLE_COUNT,
  });

  const isCurrentRelease = selectedRelease.version === currentVersion;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Novità di Nexus"
      widthClassName="max-w-2xl"
      footer={(
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:items-center sm:justify-end">
          {isCurrentRelease ? (
            <>
              <button
                type="button"
                onClick={onStartCurrentRelease}
                className="rounded-xl px-4 py-2.5 text-sm font-medium text-zinc-700 transition-colors hover:bg-zinc-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:text-white/70 dark:hover:bg-white/[0.08]"
              >
                Inizia
              </button>
              <button
                type="button"
                onClick={onMarkCurrentReleaseAsRead}
                className="rounded-xl bg-zinc-950 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-200"
              >
                Non mostrare più questa versione
              </button>
            </>
          ) : (
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl bg-zinc-950 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-zinc-800 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:bg-white dark:text-zinc-950 dark:hover:bg-zinc-200"
            >
              Chiudi
            </button>
          )}
        </div>
      )}
    >
      <div className="space-y-6">
        <header className="rounded-2xl bg-teal-50/80 p-5 ring-1 ring-inset ring-teal-200/80 dark:bg-teal-400/[0.08] dark:ring-teal-300/15">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
            <div className="flex min-w-0 items-start gap-3">
              <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-teal-100 text-teal-700 dark:bg-teal-300/15 dark:text-teal-200">
                <LightBulbIcon className="h-5 w-5" aria-hidden="true" />
              </span>
              <div className="min-w-0">
                <h2 className="text-2xl font-semibold tracking-[-0.035em] text-zinc-950 dark:text-white">{selectedRelease.title}</h2>
              </div>
            </div>
            <div className="shrink-0 text-left sm:text-right">
              <p className="text-sm font-semibold tabular-nums text-zinc-900 dark:text-white">v{selectedRelease.version}</p>
              <p className="mt-1 inline-flex items-center gap-1 text-xs text-zinc-600 dark:text-white/60">
                <CalendarDaysIcon className="h-3.5 w-3.5" aria-hidden="true" />
                {formatReleaseDate(selectedRelease.releaseDate)}
              </p>
            </div>
          </div>
          <p className="mt-5 max-w-[64ch] text-sm leading-6 text-zinc-700 dark:text-white/70">{selectedRelease.summary}</p>
          {selectedKinds.length > 0 && (
            <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="Riepilogo per categoria">
              {selectedKinds.map((kind) => {
                const { Icon, label, pluralLabel, tone } = RELEASE_KIND_META[kind];
                const count = selectedKindCounts[kind];
                return (
                  <li key={kind} className={`inline-flex items-center gap-1.5 rounded-lg px-2 py-1 text-xs font-medium ring-1 ring-inset ${tone}`}>
                    <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                    <span className="tabular-nums font-semibold">{count}</span>
                    {count === 1 ? label : pluralLabel}
                  </li>
                );
              })}
            </ul>
          )}
        </header>

        <div className="grid gap-6 sm:grid-cols-[11.25rem_minmax(0,1fr)]">
          <nav aria-label="Versioni release" className="min-w-0 sm:sticky sm:top-0 sm:self-start">
            <div className="flex items-center justify-between gap-3 px-1">
              <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-zinc-500 dark:text-zinc-500">Versioni</p>
              <span className="text-xs tabular-nums text-zinc-400 dark:text-white/35">{availableReleases.length}</span>
            </div>
            <ol className="relative mt-3">
              <span className="absolute bottom-3 left-[0.6875rem] top-3 w-px bg-zinc-200 dark:bg-white/10" aria-hidden="true" />
              {timeline.map((entry) => {
                if (entry.type === 'collapsed') {
                  return (
                    <li key="collapsed" className="relative">
                      <button
                        type="button"
                        onClick={() => setTimelineExpanded(true)}
                        aria-expanded={false}
                        className="group flex w-full items-start gap-3 rounded-xl py-2 pl-0 pr-2 text-left transition-colors hover:bg-zinc-100/80 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:hover:bg-white/[0.05]"
                      >
                        <span className="relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dashed border-zinc-300 bg-white text-zinc-400 group-hover:border-teal-400 group-hover:text-teal-600 dark:border-white/20 dark:bg-zinc-900 dark:text-white/40 dark:group-hover:border-teal-300/60 dark:group-hover:text-teal-200">
                          <EllipsisHorizontalIcon className="h-3.5 w-3.5" aria-hidden="true" />
                        </span>
                        <span className="min-w-0 pt-0.5">
                          <span className="block text-sm font-medium tabular-nums text-zinc-700 dark:text-white/70">
                            {entry.versions.length} versioni precedenti
                          </span>
                          <span className="mt-0.5 block text-[11px] text-zinc-500 dark:text-white/45">Mostra tutte</span>
                          <ReleaseKindCountChips counts={entry.kindCounts} />
                        </span>
                      </button>
                    </li>
                  );
                }

                const { release, kindCounts } = entry;
                const selected = release.version === selectedRelease.version;
                const current = release.version === currentVersion;
                return (
                  <li key={release.version} className="relative">
                    <button
                      type="button"
                      onClick={() => onSelectRelease(release.version)}
                      aria-current={selected ? 'page' : undefined}
                      className={`group flex w-full items-start gap-3 rounded-xl py-2 pl-0 pr-2 text-left transition-colors focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 ${selected
                        ? 'bg-teal-50 dark:bg-teal-400/[0.08]'
                        : 'hover:bg-zinc-100/80 dark:hover:bg-white/[0.05]'}`}
                    >
                      <span
                        className={`relative z-10 flex h-6 w-6 shrink-0 items-center justify-center rounded-full ${current
                          ? 'bg-teal-600 text-white dark:bg-teal-400 dark:text-zinc-950'
                          : selected
                            ? 'bg-white ring-2 ring-teal-500 dark:bg-zinc-900 dark:ring-teal-300'
                            : 'bg-white ring-1 ring-zinc-300 dark:bg-zinc-900 dark:ring-white/20'}`}
                        aria-hidden="true"
                      >
                        {current ? (
                          <CheckIcon className="h-3.5 w-3.5" />
                        ) : (
                          <span className={`h-1.5 w-1.5 rounded-full ${selected ? 'bg-teal-500 dark:bg-teal-300' : 'bg-zinc-300 dark:bg-white/25'}`} />
                        )}
                      </span>
                      <span className="min-w-0 pt-0.5">
                        <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <span className={`text-sm font-semibold tabular-nums ${selected ? 'text-teal-800 dark:text-teal-100' : 'text-zinc-800 dark:text-white/80'}`}>
                            v{release.version}
                          </span>
                          {current && (
                            <span className="rounded-md bg-teal-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-[0.08em] text-teal-800 dark:bg-teal-300/15 dark:text-teal-200">
                              In uso
                            </span>
                          )}
                        </span>
                        <span className="mt-0.5 block text-[11px] tabular-nums text-zinc-500 dark:text-white/45">
                          {formatReleaseDate(release.releaseDate)}
                        </span>
                        <ReleaseKindCountChips counts={kindCounts} />
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
            {timelineExpanded && availableReleases.length > TIMELINE_VISIBLE_COUNT + 1 && (
              <button
                type="button"
                onClick={() => setTimelineExpanded(false)}
                aria-expanded
                className="mt-1 w-full rounded-lg px-2 py-1.5 text-left text-xs font-medium text-zinc-500 transition-colors hover:bg-zinc-100/80 hover:text-zinc-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-teal-500 dark:text-white/45 dark:hover:bg-white/[0.05] dark:hover:text-white/70"
              >
                Mostra meno
              </button>
            )}
          </nav>

          <article aria-labelledby="release-notes-content-title" className="min-w-0">
            <h3 id="release-notes-content-title" className="sr-only">Novità della release {selectedRelease.version}</h3>
            {selectedRelease.sections.length > 0 ? (
              <div className="space-y-3">
                {selectedRelease.sections.map((section) => (
                  <section key={section.id} className="rounded-2xl bg-zinc-50/90 p-4 ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-white/[0.035] dark:ring-white/[0.08]" aria-labelledby={`release-section-${section.id}`}>
                    <div className="flex items-center justify-between gap-3">
                      <h4 id={`release-section-${section.id}`} className="text-sm font-semibold text-zinc-950 dark:text-white">{section.title}</h4>
                      <span className="text-xs tabular-nums text-zinc-400 dark:text-white/35">{section.items.length} {section.items.length === 1 ? 'aggiornamento' : 'aggiornamenti'}</span>
                    </div>
                    <ul className="mt-3 divide-y divide-zinc-200/80 dark:divide-white/[0.08]">
                      {section.items.map((item) => {
                        const { Icon, label, tone } = RELEASE_KIND_META[item.kind];
                        return (
                          <li key={item.id} className="flex gap-3 py-3 first:pt-0 last:pb-0">
                            <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-lg ring-1 ring-inset ${tone}`} title={label}>
                              <Icon className="h-3.5 w-3.5" aria-hidden="true" />
                            </span>
                            <div className="min-w-0">
                              <p className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-sm font-medium text-zinc-900 dark:text-white">
                                {item.title}
                                <span className="text-[11px] font-medium text-zinc-500 dark:text-white/45">{label}</span>
                              </p>
                              <p className="mt-1 text-sm leading-5 text-zinc-600 dark:text-white/60">{item.description}</p>
                            </div>
                          </li>
                        );
                      })}
                    </ul>
                  </section>
                ))}
              </div>
            ) : (
              <div className="rounded-2xl bg-zinc-50/90 p-5 text-sm leading-6 text-zinc-600 ring-1 ring-inset ring-zinc-950/[0.08] dark:bg-white/[0.035] dark:text-white/60 dark:ring-white/[0.08]">
                Questa release contiene aggiornamenti generali dell’interfaccia.
              </div>
            )}
          </article>
        </div>

      </div>
    </Drawer>
  );
}
